import { useRef } from 'react';
import {
  Text,
  Stack,
  Group,
  Button,
  Paper,
  Box,
  Center,
  Alert,
  RingProgress,
} from '@mantine/core';
import {
  FaVideo,
  FaTrash,
  FaUpload,
  FaStop,
  FaRedo,
  FaCheck,
  FaCircle,
  FaTimes,
} from 'react-icons/fa';
import useVideoRecorder from '@hooks/useVideoRecorder';
import {
  ACCEPTED_VIDEO_TYPES,
  MAX_FILE_SIZE_MB,
  MAX_VIDEO_DURATION_SECONDS,
  recordedBlobToFile,
  validateVideoFile,
} from '@utils/videoFile';

interface VideoRecorderInputProps {
  questionText: string;
  uploadedVideo: File | null;
  videoPreviewUrl?: string;
  onFileChange: (file: File | null) => void;
  onError: (message: string) => void;
}

/**
 * Format file size for display
 */
const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Component for recording or uploading video responses
 * Encapsulates all video recording logic and UI states
 */
function VideoRecorderInput({
  questionText,
  uploadedVideo,
  videoPreviewUrl,
  onFileChange,
  onError,
}: VideoRecorderInputProps) {
  const videoInputRef = useRef<HTMLInputElement>(null);

  const {
    state: recorderState,
    error: recorderError,
    recordedBlob,
    recordedUrl,
    elapsedSeconds,
    maxDurationSeconds,
    videoRef: webcamRef,
    isRecordingMode,
    startCamera,
    startRecording,
    stopRecording,
    retake,
    enterRecordingMode,
    exitRecordingMode,
  } = useVideoRecorder({
    maxDurationSeconds: MAX_VIDEO_DURATION_SECONDS,
  });

  const handleStartRecordingMode = async () => {
    enterRecordingMode();
    await startCamera();
  };

  const handleCancelRecordingMode = () => {
    exitRecordingMode();
  };

  const handleUseRecordedVideo = () => {
    if (recordedBlob) {
      onFileChange(recordedBlobToFile(recordedBlob, 'video-response'));
      exitRecordingMode();
    }
  };

  const handleRetakeVideo = async () => {
    retake();
    await startCamera();
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;

    const validationError = await validateVideoFile(file);
    if (validationError) {
      onError(validationError);
      input.value = '';
      return;
    }

    onFileChange(file);
  };

  // Show uploaded video preview
  if (uploadedVideo) {
    return (
      <Stack gap="md">
        <Text fw={500}>{questionText}</Text>
        <Paper p="md" withBorder>
          <Stack gap="sm">
            <Group justify="space-between">
              <Group gap="sm">
                <FaVideo size={20} />
                <Stack gap={0}>
                  <Text size="sm" fw={500} lineClamp={1}>
                    {uploadedVideo.name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {formatFileSize(uploadedVideo.size)}
                  </Text>
                </Stack>
              </Group>
              <Button
                variant="subtle"
                color="red"
                size="sm"
                leftSection={<FaTrash size={14} />}
                onClick={() => onFileChange(null)}
              >
                Remove
              </Button>
            </Group>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- Preview only, no captions needed */}
            <video
              src={videoPreviewUrl}
              controls
              style={{
                maxWidth: '100%',
                maxHeight: '200px',
                borderRadius: '8px',
              }}
            />
          </Stack>
        </Paper>
      </Stack>
    );
  }

  // Show recording mode UI
  if (isRecordingMode) {
    return (
      <Stack gap="md">
        <Text fw={500}>{questionText}</Text>

        {/* Error alert */}
        {recorderError && (
          <Alert color="red" title="Camera Error">
            {recorderError}
          </Alert>
        )}

        {/* Requesting permission state */}
        {recorderState === 'requesting-permission' && (
          <Paper p="xl" withBorder>
            <Center>
              <Stack align="center" gap="md">
                <Text>Requesting camera access...</Text>
                <Text size="sm" c="dimmed">
                  Please allow camera and microphone access when prompted
                </Text>
              </Stack>
            </Center>
          </Paper>
        )}

        {/* Camera ready - show preview and start button */}
        {recorderState === 'ready' && (
          <Stack gap="md">
            <Paper p="sm" withBorder>
              <Box
                style={{
                  position: 'relative',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: '#1a1a1a',
                  minHeight: '200px',
                }}
              >
                <video
                  ref={webcamRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    width: '100%',
                    maxHeight: '300px',
                    objectFit: 'cover',
                    transform: 'scaleX(-1)',
                    display: 'block',
                  }}
                />
              </Box>
            </Paper>
            <Text size="sm" c="dimmed" ta="center">
              Max recording time: {maxDurationSeconds} seconds
            </Text>
            <Group justify="center" gap="md">
              <Button
                variant="filled"
                color="red"
                leftSection={<FaCircle size={12} />}
                onClick={startRecording}
              >
                Start Recording
              </Button>
              <Button
                variant="default"
                leftSection={<FaTimes size={14} />}
                onClick={handleCancelRecordingMode}
              >
                Cancel
              </Button>
            </Group>
          </Stack>
        )}

        {/* Recording in progress */}
        {recorderState === 'recording' && (
          <Stack gap="md">
            <Paper p="sm" withBorder>
              <Box
                style={{
                  position: 'relative',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: '#1a1a1a',
                  minHeight: '200px',
                }}
              >
                <video
                  ref={webcamRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    width: '100%',
                    maxHeight: '300px',
                    objectFit: 'cover',
                    transform: 'scaleX(-1)',
                    display: 'block',
                  }}
                />
                {/* Recording indicator */}
                <Box
                  style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'rgba(0,0,0,0.6)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                  }}
                >
                  <FaCircle size={10} color="#ff4444" />
                  <Text size="sm" c="white" fw={500}>
                    REC
                  </Text>
                </Box>
              </Box>
            </Paper>

            {/* Controls with timer */}
            <Group justify="center" gap="md" align="center">
              <Button
                variant="filled"
                color="red"
                leftSection={<FaStop size={14} />}
                onClick={stopRecording}
              >
                Stop Recording
              </Button>
              <RingProgress
                size={50}
                thickness={4}
                roundCaps
                sections={[
                  {
                    value: (elapsedSeconds / maxDurationSeconds) * 100,
                    color: 'red',
                  },
                ]}
                label={
                  <Center>
                    <Text size="xs" fw={600}>
                      {elapsedSeconds}s
                    </Text>
                  </Center>
                }
              />
            </Group>
          </Stack>
        )}

        {/* Recording stopped - show preview */}
        {recorderState === 'stopped' && recordedUrl && recordedBlob && (
          <Stack gap="md">
            <Paper p="sm" withBorder>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption -- Preview only, no captions needed */}
              <video
                src={recordedUrl}
                controls
                style={{
                  width: '100%',
                  maxHeight: '300px',
                  borderRadius: '8px',
                }}
              />
            </Paper>
            <Text size="sm" c="dimmed" ta="center">
              Recording size: {formatFileSize(recordedBlob.size)}
            </Text>
            <Group justify="center" gap="md">
              <Button
                variant="filled"
                leftSection={<FaCheck size={14} />}
                onClick={handleUseRecordedVideo}
              >
                Use This Video
              </Button>
              <Button
                variant="default"
                leftSection={<FaRedo size={14} />}
                onClick={handleRetakeVideo}
              >
                Retake
              </Button>
            </Group>
          </Stack>
        )}

        {/* Error state with retry */}
        {recorderState === 'error' && (
          <Stack gap="md">
            <Group justify="center" gap="md">
              <Button variant="outline" onClick={handleStartRecordingMode}>
                Try Again
              </Button>
              <Button variant="default" onClick={handleCancelRecordingMode}>
                Cancel
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    );
  }

  // Initial state - show record or upload options
  return (
    <Stack gap="md">
      <Text fw={500}>{questionText}</Text>
      <Text size="sm" c="dimmed">
        Or upload a file. Accepted formats: MP4, WebM, MOV, OGG (max{' '}
        {MAX_FILE_SIZE_MB} MB)
      </Text>

      <Group gap="md">
        <Button
          variant="filled"
          leftSection={<FaVideo size={14} />}
          onClick={handleStartRecordingMode}
        >
          Record Video
        </Button>

        <input
          ref={videoInputRef}
          type="file"
          accept={ACCEPTED_VIDEO_TYPES}
          onChange={handleVideoUpload}
          style={{ display: 'none' }}
        />
        <Button
          variant="outline"
          leftSection={<FaUpload size={14} />}
          onClick={() => videoInputRef.current?.click()}
        >
          Upload Video
        </Button>
      </Group>
    </Stack>
  );
}

export default VideoRecorderInput;
